






















































import { getSafePropertyChain } from '@empathy/get-safe-property-chain';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Messages } from '../../../i18n';
import { AnimateSizeToAuto } from '../utils';
import { collapseEvent, expandEvent, itemSelectedEvent } from './dropdown.const';

type MapTitle<T> = (item: T, messages: Messages) => string;

@Component
export default class Dropdown<T> extends Vue {

  @Prop({ required: true })
  items: T[];

  @Prop({ default: '{selectedItem}' })
  toggleButtonMessageHTML: string;

  @Prop({ default: '' })
  noSelectionToggleButtonMessageHTML: string;

  @Prop({ type: [String, Function], default: () => (item: T) => item.toString() })
  itemTitleProperty: string | MapTitle<T>;

  @Prop({ default: 'ebx-dropdown' })
  id: string;

  @Prop({ default: '' })
  ariaLabel: string;

  @Prop({ required: true })
  expanded: boolean;

  @Prop({ required: true })
  selectedItemIndex: number;

  @Prop({ default: 250 })
  clearSearchBufferDebounce: number;

  @Prop({ default: false })
  expandOnHover: boolean;

  highlightedItemIndex = this.selectedItemIndex;
  searchBuffer = '';
  animateHeightToAuto = new AnimateSizeToAuto();
  clearSearchBufferTimeoutId: number;

  $refs: {
    dropdownButton: HTMLButtonElement;
    list: HTMLUListElement;
  };

  get hoverHandlers() {
    return this.expandOnHover ? {
      mouseenter: this.expandDropdown,
      mouseleave: this.collapseDropdown
    } : {};
  }

  get toggleDropdownMessage(): string {
    return this.$options.filters.compileMessage(this.toggleButtonMessageHTML,
      { selectedItem: this.titles[this.highlightedItemIndex] || this.noSelectionToggleButtonMessageHTML });
  }

  get titles(): string[] {
    // I'm not sure why I need to cast these types, but TypeScript 2.9.1 throws an error if I don't
    if (typeof this.itemTitleProperty === 'function') {
      return this.items.map(item => (this.itemTitleProperty as MapTitle<T>)(item, this.$x.messages));
    } else {
      return this.items.map(item => getSafePropertyChain(item, this.itemTitleProperty as string));
    }
  }

  private toggleDropdown() {
    if (this.expanded) {
      this.collapseDropdown();
    } else {
      this.expandDropdown();
    }
  }

  private expandDropdown() {
    this.$emit(expandEvent);
    this.$nextTick().then(() => this.$refs.list.focus());
  }

  private collapseDropdown() {
    this.$emit(collapseEvent);
  }

  private resetToSelectedItemAndFocusButton() {
    this.highlightSelectedItem();
    this.collapseDropdown();
    this.focusToggleButton();
  }

  private selectHighlightedItemAndFocusButton() {
    this.$emit(itemSelectedEvent, this.highlightedItemIndex);
    this.focusToggleButton();
  }

  private collapseIfNotClickedOnToggleAndReset(event: FocusEvent) {
    this.highlightSelectedItem();
    if (event.relatedTarget !== this.$refs.dropdownButton) {
      this.collapseDropdown();
    }
  }

  private expandHighlightingPreviousSelectedItem() {
    this.expandDropdown();
    this.highlightPreviousItem();
  }

  private expandHighlightingNextSelectedItem() {
    this.expandDropdown();
    this.highlightNextItem();
  }

  private highlightNextItem() {
    this.highlightedItemIndex = (this.highlightedItemIndex + 1) % this.items.length;
  }

  private highlightPreviousItem() {
    this.highlightedItemIndex = this.highlightedItemIndex > 0 ? this.highlightedItemIndex - 1 : this.items.length - 1;
  }

  private highlightFirstItem() {
    this.highlightedItemIndex = 0;
  }

  private highlightLastItem() {
    this.highlightedItemIndex = this.items.length - 1;
  }

  @Watch('selectedItemIndex')
  private highlightSelectedItem() {
    this.highlightedItemIndex = this.selectedItemIndex;
  }

  private highlightFirstOccurrence(event: KeyboardEvent) {
    const pressedKey = String.fromCharCode(event.keyCode);
    if (/\w/.test(pressedKey)) {
      this.searchFirstItemMatching(this.searchBuffer + pressedKey);
    } else {
      this.resetSearchBuffer();
    }
    clearTimeout(this.clearSearchBufferTimeoutId);
    this.clearSearchBufferTimeoutId = window.setTimeout(this.resetSearchBuffer, this.clearSearchBufferDebounce);
  }

  private searchFirstItemMatching(search: string, initialIndex = this.highlightedItemIndex + 1) {
    this.searchBuffer = search;
    const searchItems = this.titles.slice(initialIndex);
    const itemIndexToHighlight = searchItems.findIndex(title => title.startsWith(search));
    if (itemIndexToHighlight !== -1) {
      this.highlightedItemIndex = itemIndexToHighlight + initialIndex;
    } else if (initialIndex !== 0) {
      this.searchFirstItemMatching(search, 0);
    }
  }

  private resetSearchBuffer() {
    this.searchBuffer = '';
  }

  private selectItemByIndex(index: number) {
    this.$emit(itemSelectedEvent, index);
    this.highlightedItemIndex = index;
    this.focusToggleButton();
  }

  private focusToggleButton() {
    this.$refs.dropdownButton.focus();
  }
}
